import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Facebook, MapPin, Mail, Phone, Send } from "lucide-react";
import { api, assetUrl } from "../lib/api";
import { FacebookEmbed, FacebookLikeButton } from "../components/FacebookEmbed";
import { toast } from "sonner";

// Page satellite "RADO PRODUCTION" — studio partenaire indépendant.
// Minimaliste : hero (logo + cover + bio courte) + flux Facebook + bouton "Suivre".
// Configurable via /admin → onglet Arnak → sous-onglet RADO (clés arnak_settings rado_*).

const fmtCompact = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return "";
  if (v < 1000) return String(Math.round(v));
  if (v < 10000) return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 }).format(v / 1000) + "K";
  if (v < 1000000) return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(v / 1000) + "K";
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 }).format(v / 1000000) + "M";
};

export default function RadoProductionPage() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const isArnakDomain = typeof window !== "undefined" && /arnakclan\.com$/i.test(window.location.hostname);
  const backHref = isArnakDomain ? "/" : "/arnak";

  useEffect(() => {
    document.title = "RADO Production — Studio · Toamasina";
    api.get("/arnak/public").then(({ data }) => {
      setS(data.settings || {});
      setLoading(false);
    }).catch(() => {
      toast.error("Erreur de chargement");
      setLoading(false);
    });
  }, []);

  if (loading) return <div className="min-h-screen bg-black text-white flex items-center justify-center text-sm">Chargement…</div>;
  if (!s) return null;

  const name      = s.rado_name || "RADO Production";
  const tagline   = s.rado_tagline || "Studio de production · Toamasina";
  const bio       = s.rado_bio || "Studio d'enregistrement et de production basé à Toamasina (Madagascar). Beatmaking, mixage, mastering, captation vidéo.";
  const logoUrl   = assetUrl(s.rado_logo_url || "");
  const coverUrl  = assetUrl(s.rado_cover_url || "");
  const fbUrl     = s.rado_fb_url || "https://www.facebook.com/Radostudiopro";
  const fbCount   = s.rado_facebook_followers_count;
  const email     = s.rado_email || "";
  const phone     = s.rado_phone || "";
  const whatsapp  = s.rado_whatsapp || "";
  const city      = s.rado_city || "Toamasina, Madagascar";

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white overflow-x-hidden" data-testid="rado-page">
      {/* TOP NAV minimaliste */}
      <nav className="sticky top-0 z-40 bg-black/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-4 flex items-center justify-between gap-3">
          <Link
            to={backHref}
            className="inline-flex items-center gap-2 text-white/70 hover:text-yellow-400 transition text-[11px] uppercase tracking-widest font-bold"
            data-testid="rado-back-link"
          >
            <ArrowLeft size={14} /> Retour ARNAK
          </Link>
          {fbCount && fmtCompact(fbCount) && (
            <a
              href={fbUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1877F2]/15 hover:bg-[#1877F2]/25 border border-[#1877F2]/40 hover:border-[#1877F2]/70 text-white transition"
              title="Suivre RADO Production sur Facebook"
              data-testid="rado-nav-fb-followers"
            >
              <Facebook size={14} className="text-[#4d92ff]" fill="currentColor" />
              <span className="text-[11px] font-black uppercase tracking-wider leading-none">
                {fmtCompact(fbCount)}
                <span className="ml-1 text-white/70 font-bold normal-case tracking-normal">abonnés</span>
              </span>
            </a>
          )}
        </div>
      </nav>

      {/* HERO */}
      <header
        className="relative px-6 md:px-12 py-16 md:py-24 border-b border-white/10 overflow-hidden"
        style={coverUrl ? {
          backgroundImage: `linear-gradient(180deg, rgba(10,10,15,0.65) 0%, rgba(10,10,15,0.95) 100%), url(${coverUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        } : undefined}
      >
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row md:items-end gap-8 md:gap-12 relative">
          {logoUrl && (
            <img
              src={logoUrl}
              alt={name}
              className="h-32 md:h-44 w-auto object-contain drop-shadow-[0_8px_30px_rgba(0,0,0,0.6)] shrink-0"
              data-testid="rado-logo"
            />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.4em] font-bold text-yellow-400/80 mb-3" data-testid="rado-tagline">
              {tagline}
            </p>
            <h1
              className="font-black text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-[0.95] mb-4"
              style={{ fontFamily: "'Anton', sans-serif" }}
              data-testid="rado-title"
            >
              {name}
            </h1>
            {city && (
              <p className="inline-flex items-center gap-1.5 text-white/65 text-sm mb-5">
                <MapPin size={14} className="text-yellow-400" /> {city}
              </p>
            )}
            <p className="text-white/80 text-base md:text-lg max-w-2xl leading-relaxed" data-testid="rado-bio">
              {bio}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <FacebookLikeButton url={fbUrl} label="Suivre RADO Production" />
              {email && (
                <a
                  href={`mailto:${email}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-sm font-bold transition"
                  data-testid="rado-email"
                >
                  <Mail size={14} /> Écrire au studio
                </a>
              )}
              {phone && (
                <a
                  href={`tel:${phone}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-sm font-bold transition"
                  data-testid="rado-phone"
                >
                  <Phone size={14} /> {phone}
                </a>
              )}
              {whatsapp && (
                <a
                  href={`https://wa.me/${whatsapp.replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366]/20 hover:bg-[#25D366]/35 text-sm font-bold transition"
                  data-testid="rado-whatsapp"
                >
                  <Send size={14} /> WhatsApp
                </a>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* FLUX FACEBOOK */}
      <section className="px-6 md:px-12 py-14 md:py-20" data-testid="rado-facebook-feed">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-7">
            <div>
              <p className="text-[10px] uppercase tracking-[0.4em] font-bold text-white/40">Actualités</p>
              <h2
                className="font-black text-3xl md:text-4xl tracking-tighter mt-2 inline-flex items-center gap-3"
                style={{ fontFamily: "'Anton', sans-serif" }}
              >
                <span className="w-2.5 h-2.5 bg-[#1877F2] rounded-full animate-pulse" />
                Direct depuis Facebook
              </h2>
              <p className="text-white/55 text-sm mt-2">Les dernières publications du studio RADO Production.</p>
            </div>
            <FacebookLikeButton url={fbUrl} label="Suivre le studio" />
          </div>

          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-3 md:p-4 overflow-hidden">
            <FacebookEmbed url={fbUrl} height={780} />
          </div>

          <p className="text-center text-white/40 text-xs mt-5">
            Tu peux interagir, commenter et partager directement depuis Facebook en cliquant sur les publications ci-dessus.
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-6 md:px-12 py-12 border-t border-white/5 bg-black/30">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Carte contact pro */}
          {(email || phone || whatsapp) && (
            <div className="bg-gradient-to-br from-yellow-400/[0.06] to-transparent border border-yellow-400/20 rounded-2xl p-5 md:p-7" data-testid="rado-footer-contact">
              <p className="text-[10px] uppercase tracking-[0.4em] font-bold text-yellow-400 mb-2">Réserver une session</p>
              <h3
                className="font-black text-2xl md:text-3xl tracking-tight mb-4"
                style={{ fontFamily: "'Anton', sans-serif" }}
              >
                Contactez le studio directement
              </h3>
              <div className="grid sm:grid-cols-3 gap-3">
                {phone && (
                  <a
                    href={`tel:${phone}`}
                    className="flex items-center gap-3 px-4 py-3.5 rounded-lg bg-white/[0.04] hover:bg-white/10 border border-white/10 hover:border-yellow-400/40 transition group"
                  >
                    <span className="w-9 h-9 rounded-full bg-yellow-400/15 flex items-center justify-center shrink-0 group-hover:bg-yellow-400/25 transition">
                      <Phone size={15} className="text-yellow-400" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-widest text-white/45 font-bold">Téléphone</p>
                      <p className="text-sm font-bold truncate">{phone}</p>
                    </div>
                  </a>
                )}
                {whatsapp && (
                  <a
                    href={`https://wa.me/${whatsapp.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 px-4 py-3.5 rounded-lg bg-white/[0.04] hover:bg-[#25D366]/15 border border-white/10 hover:border-[#25D366]/40 transition group"
                  >
                    <span className="w-9 h-9 rounded-full bg-[#25D366]/15 flex items-center justify-center shrink-0 group-hover:bg-[#25D366]/30 transition">
                      <Send size={15} className="text-[#25D366]" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-widest text-white/45 font-bold">WhatsApp</p>
                      <p className="text-sm font-bold truncate">{whatsapp}</p>
                    </div>
                  </a>
                )}
                {email && (
                  <a
                    href={`mailto:${email}`}
                    className="flex items-center gap-3 px-4 py-3.5 rounded-lg bg-white/[0.04] hover:bg-white/10 border border-white/10 hover:border-yellow-400/40 transition group"
                  >
                    <span className="w-9 h-9 rounded-full bg-yellow-400/15 flex items-center justify-center shrink-0 group-hover:bg-yellow-400/25 transition">
                      <Mail size={15} className="text-yellow-400" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-widest text-white/45 font-bold">Email</p>
                      <p className="text-sm font-bold truncate">{email}</p>
                    </div>
                  </a>
                )}
              </div>
            </div>
          )}
          <div className="text-center text-[10px] uppercase tracking-[0.3em] text-white/30 space-y-2">
            <p>© {new Date().getFullYear()} {name} · Studio partenaire indépendant.</p>
            <p>
              <Link to={backHref} className="hover:text-yellow-400 transition" data-testid="rado-footer-back">
                ← Retour sur arnakclan.com
              </Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
